import { useState } from "react";
import { FormProvider, UseFormReturn } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import moment from "moment";

import "./Comments.scss";
import { deleteQuoteRequestCommentAttachment } from "api";
import { quoteRequestQueryKeys } from "api/queries/quotes/quoteRequestQueryKeys";
import { useGetShipmentBasic } from "api/queries/shipments/useGetShipmentBasic";
import { ReactComponent as MessageIcon } from "assets/images/iconsV2/message-square-02.svg";
import CommentField from "components/common/CommentFIeld/CommentField";
import CommentItem from "components/common/CommentItem/CommentItem";
import TabbedSection from "components/common/TabbedSection/TabbedSection";
import FilePreview from "components/v2/FilePreview/FilePreview";
import errorToast from "helpers/errorToast";
import useConfirm from "hooks/useConfirm";
import useFileHook from "hooks/useFileHook";
import usePermissions from "hooks/usePermissions";

const MAX_FILE_COUNT = 10;

interface CommentsProps {
  methods: UseFormReturn<
    {
      selected_message_audience: string;
      comment_message: string;
    },
    object
  >;
  textAreaRef: React.RefObject<HTMLTextAreaElement>;
  onSubmitComment: () => void;
  isSubmitting: boolean;
  audienceOptions?: {
    value: string;
    label: string;
    sublabel?: string;
    group?: string;
  }[];
  comments?: any[];
  fileProps: ReturnType<typeof useFileHook>;
  isQuickRequest?: boolean;
  isInternalUsersOnly?: boolean;
}

type QuoteCommentFile = {
  filename: string;
  attachment: string;
  size: string;
  reference_number?: string;
  canDelete: boolean;
};

const hasCommentContent = (comment: { comment?: string; attachments?: unknown[]; deleted_attachments?: unknown[] }) => {
  const hasText = Boolean(comment.comment?.trim());
  const hasAttachments = Boolean(comment.attachments?.length);
  const hasDeletedAttachments = Boolean(comment.deleted_attachments?.length);
  return hasText || hasAttachments || hasDeletedAttachments;
};

function Comments({
  methods,
  textAreaRef,
  onSubmitComment,
  isSubmitting,
  audienceOptions = [],
  comments = [],
  fileProps,
  isQuickRequest,
  isInternalUsersOnly = false,
}: CommentsProps) {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { confirm, ConfirmModal } = useConfirm();
  const { id } = useParams();
  const shipmentId = id!;
  const { permissions } = usePermissions();
  const { full_name: currentUserFullName, company_name: currentUserCompanyName } = JSON.parse(permissions || "{}");

  const [activeKey, setActiveKey] = useState("comments");
  const [selectedFile, setSelectedFile] = useState<QuoteCommentFile | null>(null);

  const handleTabSelect = (key: string) => {
    setActiveKey(key);
  };

  const { data: shipmentData, isLoading: isLoadingShipmentBasic }: any = useGetShipmentBasic(shipmentId, {
    enabled: !!shipmentId,
    refetchOnMount: false,
  });
  const availableForComments = shipmentData?.shipment.available_for_comments;

  const {
    files,
    filesSortedByErrorsFirst,
    handlePaste,
    dragging,
    draggableProps,
    handleRemoveFile,
    handleFileUpload,
    hiddenFileInputRef,
  } = fileProps;

  const visibleComments = comments?.filter(hasCommentContent) ?? [];

  const uploadedFiles: QuoteCommentFile[] =
    visibleComments
      ?.flatMap((comment) =>
        (comment.attachments || []).map((attachment: any) => ({
          filename: attachment.filename,
          attachment: attachment.attachment,
          size: attachment.size,
          reference_number: attachment.reference_number,
          canDelete: Boolean(
            attachment.reference_number &&
              currentUserFullName &&
              currentUserCompanyName &&
              comment.posted_by?.name === currentUserFullName &&
              comment.posted_by?.company === currentUserCompanyName,
          ),
        })),
      )
      ?.filter(Boolean) || [];

  const { mutate: deleteCommentFile, isLoading: isDeletingFile } = useMutation({
    mutationFn: (referenceNumber?: string) => deleteQuoteRequestCommentAttachment(shipmentId, referenceNumber),
    onSuccess: () => {
      toast(
        intl.formatMessage({
          id: "tracking.activity.deleteFileDialog.deleteSuccess",
          defaultMessage: "File successfully deleted.",
        }),
        { type: "success" },
      );
      setSelectedFile(null);
      queryClient.invalidateQueries({
        queryKey: quoteRequestQueryKeys.quoteCommentsListing(shipmentId),
      });
    },
    onError: (err) => {
      setSelectedFile(null);
      errorToast(
        err,
        intl.formatMessage({
          id: "tracking.activity.deleteFileDialog.deleteFail",
          defaultMessage: "Could not delete file. Please try again later.",
        }),
      );
    },
  });

  const handleClickDeleteFile = async (file: QuoteCommentFile) => {
    const confirmed = await confirm({
      title: intl.formatMessage({
        id: "tracking.activity.deleteFileDialog.title",
        defaultMessage: "Delete File?",
      }),
      message: intl.formatMessage({
        id: "tracking.activity.deleteFileDialog.subtitle",
        defaultMessage: "This file will be deleted for everyone.",
      }),
      confirmBtnText: intl.formatMessage({
        id: "general.delete",
        defaultMessage: "Delete",
      }),
      cancelBtnText: intl.formatMessage({
        id: "general.back",
        defaultMessage: "Back",
      }),
    });

    if (!confirmed) return;

    setSelectedFile(file);
    deleteCommentFile(file.reference_number);
  };

  const tabs = [
    {
      key: "comments",
      title: intl.formatMessage({ id: "general.comments", defaultMessage: "Comments" }),
      content: (
        <>
          {(availableForComments || isQuickRequest || isInternalUsersOnly) &&
            (isQuickRequest ? (
              <CommentField
                disableFileInput
                textAreaRef={textAreaRef}
                onSubmit={onSubmitComment}
                isSubmitting={isSubmitting}
                audienceOptions={audienceOptions}
                isQuickRequest={isQuickRequest}
                isInternalUsersOnly={isInternalUsersOnly}
              />
            ) : (
              <CommentField
                dragging={dragging}
                draggableProps={draggableProps}
                textAreaRef={textAreaRef}
                onPaste={handlePaste}
                files={files}
                filesSortedByErrorsFirst={filesSortedByErrorsFirst}
                onFileRemove={handleRemoveFile}
                maxFileCount={MAX_FILE_COUNT}
                hiddenFileInputRef={hiddenFileInputRef}
                onFileUpload={handleFileUpload}
                onSubmit={onSubmitComment}
                isSubmitting={isSubmitting}
                audienceOptions={audienceOptions}
                isQuickRequest={isQuickRequest}
                isInternalUsersOnly={isInternalUsersOnly}
              />
            ))}
          {visibleComments.length === 0 && (
            <div className="d-flex flex-column align-items-center justify-content-center mt-3xl">
              <MessageIcon />
              <p className="font-semibold text-md mt-lg">
                <FormattedMessage id="quote-request.comments.noCommentsYet" defaultMessage="No comments yet" />
              </p>
              <p className="mt-xs text-tertiary">
                <FormattedMessage
                  id="quote-request.comments.commentsWillShowUpHere"
                  defaultMessage="Comments will show up here"
                />
              </p>
            </div>
          )}
          {visibleComments
            ?.sort((a, b) => moment(b.posted_at).diff(moment(a.posted_at)))
            ?.map((comment: any, idx: number) => (
              <CommentItem
                key={idx}
                message={comment.comment}
                isUserComment
                authorName={comment.posted_by?.name}
                companyName={comment.posted_by?.company}
                logo={comment.posted_by?.logo_url}
                timestamp={comment.posted_at}
                audience={comment.posted_to || "all"}
                files={
                  comment.attachments?.map((attachment: any) => ({
                    name: attachment.filename,
                    url: attachment.attachment,
                    size: attachment.size,
                  })) || []
                }
                deletedAttachments={comment.deleted_attachments}
              />
            ))}
        </>
      ),
    },
    ...(!isQuickRequest
      ? [
          {
            key: "files",
            title: intl.formatMessage({ id: "general.files", defaultMessage: "Files" }),
            content: (
              <div className="activity-files">
                {uploadedFiles.map((file, index) => (
                  <FilePreview
                    key={file.reference_number || index}
                    fileName={file.filename}
                    url={file.attachment}
                    fileSize={file.size}
                    onClose={file.canDelete ? () => handleClickDeleteFile(file) : undefined}
                    isLoading={isDeletingFile && selectedFile?.reference_number === file.reference_number}
                  />
                ))}
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <FormProvider {...methods}>
      <div className="h-100">
        <ConfirmModal />
        <TabbedSection
          activeKey={activeKey}
          isLoading={isLoadingShipmentBasic}
          onTabSelect={handleTabSelect}
          tabs={tabs}
          title={intl.formatMessage({
            id: "general.Comments",
            defaultMessage: "Comments",
          })}
          className="h-100"
        />
      </div>
    </FormProvider>
  );
}

export default Comments;

Comments.defaultProps = {
  audienceOptions: [],
  comments: [],
};
